import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, Facebook, Instagram, Youtube, MapPin, Menu, X } from 'lucide-react';

interface Props {
  children: React.ReactNode;
}

export const PublicLayout: React.FC<Props> = ({ children }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white font-sans flex flex-col relative">
      {/* Header - Transparent & Overlaid */}
      <header className="absolute top-0 left-0 w-full z-50 border-b border-white/10 bg-gradient-to-b from-black/50 to-transparent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-24">
            {/* Left Side: Logo + Navigation */}
            <div className="flex items-center gap-12">
              <Link to="/" className="flex-shrink-0">
                <img
                  src="/assets/go2-logo.png"
                  alt="Go2 Logo"
                  className="h-12 w-auto brightness-0 invert"
                />
              </Link>
              <nav className="hidden md:flex space-x-8">
                <Link to="/" className="text-white hover:text-go2-yellow font-bold uppercase text-sm tracking-wide transition-colors">Početna</Link>
                <Link to="/go2-legende/dashboard" className="text-white hover:text-go2-yellow font-bold uppercase text-sm tracking-wide transition-colors">Komandna tabla</Link>
                <Link to="/pravila" className="text-white hover:text-go2-yellow font-bold uppercase text-sm tracking-wide transition-colors">Pravila</Link>
                <Link to="/faq" className="text-white hover:text-go2-yellow font-bold uppercase text-sm tracking-wide transition-colors">FAQ</Link>
              </nav>
            </div>

            {/* Right Side: Contact + Socials + CTA */}
            <div className="flex items-center gap-4 md:gap-8">
              <div className="hidden lg:flex items-center text-white/90 text-sm font-medium gap-6">
                <a href="tel:+381113343680" className="flex items-center gap-2 hover:text-go2-yellow transition-colors">
                  <Phone size={16} className="text-go2-yellow" />
                  <span>+381 11 33 43 680</span>
                </a>
                <div className="flex items-center gap-4">
                  <a href="https://www.facebook.com/Go2EventBeograd" target="_blank" rel="noopener noreferrer" className="hover:text-go2-yellow transition-colors"><Facebook size={18} /></a>
                  <a href="https://www.instagram.com/go2travelling/" target="_blank" rel="noopener noreferrer" className="hover:text-go2-yellow transition-colors"><Instagram size={18} /></a>
                  <a href="https://www.youtube.com/@go2event" target="_blank" rel="noopener noreferrer" className="hover:text-go2-yellow transition-colors"><Youtube size={18} /></a>
                </div>
              </div>

              <div className="hidden sm:flex items-center gap-4">
                <Link to="/prijava" className="text-white hover:text-go2-yellow font-bold text-sm uppercase">Prijava</Link>
                <Link
                  to="/registracija"
                  className="bg-go2-pink text-white px-6 py-2 rounded-full font-bold text-sm uppercase hover:bg-brand-600 transition-colors shadow-md border border-transparent hover:border-white/20"
                >
                  Registracija
                </Link>
              </div>

              {/* Mobile Hamburger */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden text-white p-2"
                aria-label="Meni"
              >
                {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-black/95 backdrop-blur-sm border-t border-white/10 animate-in">
            <nav className="max-w-7xl mx-auto px-4 py-6 flex flex-col space-y-4">
              <Link to="/" onClick={() => setMobileMenuOpen(false)} className="text-white hover:text-go2-yellow font-bold uppercase text-base tracking-wide transition-colors py-2">Početna</Link>
              <Link to="/go2-legende/dashboard" onClick={() => setMobileMenuOpen(false)} className="text-white hover:text-go2-yellow font-bold uppercase text-base tracking-wide transition-colors py-2">Komandna tabla</Link>
              <Link to="/pravila" onClick={() => setMobileMenuOpen(false)} className="text-white hover:text-go2-yellow font-bold uppercase text-base tracking-wide transition-colors py-2">Pravila</Link>
              <Link to="/faq" onClick={() => setMobileMenuOpen(false)} className="text-white hover:text-go2-yellow font-bold uppercase text-base tracking-wide transition-colors py-2">FAQ</Link>
              <div className="border-t border-white/20 pt-4 flex flex-col space-y-3">
                <Link
                  to="/prijava"
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-white hover:text-go2-yellow font-bold uppercase text-base tracking-wide transition-colors py-2"
                >
                  Prijava
                </Link>
                <Link
                  to="/registracija"
                  onClick={() => setMobileMenuOpen(false)}
                  className="bg-go2-pink text-white px-6 py-3 rounded-full font-bold text-sm uppercase text-center hover:bg-brand-600 transition-colors shadow-md"
                >
                  Registracija
                </Link>
                <a href="tel:+381113343680" className="text-white/80 flex items-center gap-2 text-sm">
                  <Phone size={16} className="text-go2-yellow" />
                  +381 11 33 43 680
                </a>
                <a href="mailto:office@go2.rs" className="text-white/80 flex items-center gap-2 text-sm">
                  <Mail size={16} className="text-go2-yellow" />
                  office@go2.rs
                </a>
                <div className="flex space-x-4 pt-2">
                  <a href="https://www.facebook.com/Go2EventBeograd" target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-go2-yellow"><Facebook size={20} /></a>
                  <a href="https://www.instagram.com/go2travelling/" target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-go2-yellow"><Instagram size={20} /></a>
                  <a href="https://www.youtube.com/@go2event" target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-go2-yellow"><Youtube size={20} /></a>
                </div>
              </div>
            </nav>
          </div>
        )}
      </header>

      <main className="flex-grow">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-[#1a2b49] text-white pt-16 pb-8 border-t-4 border-go2-yellow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            <div>
              <img src="/assets/go2-logo.png" alt="Go2 Logo" className="h-8 w-auto mb-6 brightness-0 invert" />
              <p className="text-gray-400 text-sm leading-relaxed mb-6">
                Go2 Travelling je turistička agencija specijalizovana za omladinski turizam. Vodimo vas na najbolje destinacije širom Evrope i sveta.
              </p>
              <div className="flex space-x-4">
                <a href="https://www.facebook.com/Go2EventBeograd" target="_blank" rel="noopener noreferrer" className="bg-gray-800 p-2 rounded-full hover:bg-go2-blue transition-colors"><Facebook size={18} /></a>
                <a href="https://www.instagram.com/go2travelling/" target="_blank" rel="noopener noreferrer" className="bg-gray-800 p-2 rounded-full hover:bg-go2-blue transition-colors"><Instagram size={18} /></a>
                <a href="https://www.youtube.com/@go2event" target="_blank" rel="noopener noreferrer" className="bg-gray-800 p-2 rounded-full hover:bg-go2-blue transition-colors"><Youtube size={18} /></a>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-bold mb-6 text-white uppercase border-b-2 border-go2-blue inline-block pb-2">Program</h3>
              <ul className="space-y-3 text-gray-400 text-sm">
                <li><Link to="/" className="hover:text-go2-yellow transition-colors flex items-center">Početna</Link></li>
                <li><Link to="/pravila" className="hover:text-go2-yellow transition-colors flex items-center">Pravila programa</Link></li>
                <li><Link to="/faq" className="hover:text-go2-yellow transition-colors flex items-center">Česta pitanja</Link></li>
                <li><Link to="/go2-legende/dashboard" className="hover:text-go2-yellow transition-colors flex items-center">Moj nalog</Link></li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-bold mb-6 text-white uppercase border-b-2 border-go2-blue inline-block pb-2">Kontakt</h3>
              <ul className="space-y-4 text-gray-400 text-sm">
                <li className="flex items-start space-x-3">
                  <MapPin size={18} className="text-go2-blue mt-1 flex-shrink-0" />
                  <span>Strahinjića Bana 36,<br/>11000 Beograd, Srbija</span>
                </li>
                <li>
                  <a href="tel:+381113343680" className="flex items-center space-x-3 hover:text-go2-yellow transition-colors">
                    <Phone size={18} className="text-go2-blue flex-shrink-0" />
                    <span>+381 11 33 43 680</span>
                  </a>
                </li>
                <li>
                  <a href="mailto:office@go2.rs" className="flex items-center space-x-3 hover:text-go2-yellow transition-colors">
                    <Mail size={18} className="text-go2-blue flex-shrink-0" />
                    <span>office@go2.rs</span>
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-bold mb-6 text-white uppercase border-b-2 border-go2-blue inline-block pb-2">Radno Vreme</h3>
              <ul className="space-y-3 text-gray-400 text-sm">
                <li className="flex justify-between">
                  <span>Ponedeljak - Petak:</span>
                  <span className="text-white">10h - 18h</span>
                </li>
                <li className="flex justify-between">
                  <span>Subota:</span>
                  <span className="text-white">10h - 15h</span>
                </li>
                <li className="flex justify-between">
                  <span>Nedelja:</span>
                  <span className="text-go2-pink">Zatvoreno</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center text-sm text-gray-500">
            <p>&copy; {new Date().getFullYear()} Go2 Event d.o.o. Sva prava zadržana.</p>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <Link to="/pravila" className="hover:text-white transition-colors">Politika privatnosti</Link>
              <Link to="/pravila" className="hover:text-white transition-colors">Uslovi korišćenja</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};
